/**
 * FilterPanel - Panel lateral de filtros
 * Para aplicar filtros dinámicos a listados
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import {
  FilterList,
  ExpandMore,
  ExpandLess,
  Clear,
} from '@mui/icons-material';

/**
 * @component FilterPanel
 * @description Panel de filtros con diferentes tipos de campos
 * @param {Object} props
 * @param {Array} props.filters - Configuración de filtros
 *   Ejemplo: [
 *     { name: 'estado', label: 'Estado', type: 'select', options: [...] },
 *     { name: 'cliente', label: 'Cliente', type: 'text' },
 *   ]
 * @param {Object} props.values - Valores actuales de los filtros
 * @param {Function} props.onChange - Callback al cambiar filtros
 * @param {Function} [props.onClear] - Callback al limpiar filtros
 * @param {boolean} [props.collapsible=true] - Si el panel es colapsable
 * @param {string} [props.title='Filtros'] - Título del panel
 */
const FilterPanel = ({
  filters,
  values,
  onChange,
  onClear,
  collapsible = true,
  title = 'Filtros',
}) => {
  const [collapsed, setCollapsed] = useState(false);

  /**
   * Manejar cambio en un filtro
   */
  const handleFilterChange = (filterName, value) => {
    onChange({
      ...values,
      [filterName]: value,
    });
  };

  /**
   * Limpiar todos los filtros
   */
  const handleClearAll = () => {
    const clearedValues = {};
    filters.forEach((filter) => {
      clearedValues[filter.name] = '';
    });
    onChange(clearedValues);
    if (onClear) {
      onClear();
    }
  };

  /**
   * Contar filtros activos
   */
  const activeFiltersCount = Object.values(values).filter(
    (value) => value !== '' && value !== null && value !== undefined
  ).length;

  /**
   * Renderizar campo de filtro según tipo
   */
  const renderFilterField = (filter) => {
    const value = values[filter.name] || '';

    switch (filter.type) {
      case 'select':
        return (
          <FormControl fullWidth size="small" key={filter.name}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFilterChange(filter.name, e.target.value)}
              label={filter.label}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'text':
      default:
        return (
          <TextField
            key={filter.name}
            fullWidth
            size="small"
            label={filter.label}
            value={value}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
            placeholder={filter.placeholder}
          />
        );
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: collapsed ? 0 : 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              color="primary"
              sx={{ height: 20, minWidth: 20 }}
            />
          )}
        </Box>

        {collapsible && (
          <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        )}
      </Box>

      {/* Contenido */}
      <Collapse in={!collapsed}>
        <Divider sx={{ mb: 2 }} />

        {/* Campos de filtro */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filters.map((filter) => renderFilterField(filter))}
        </Box>

        {/* Botón limpiar */}
        {activeFiltersCount > 0 && (
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<Clear />}
            onClick={handleClearAll}
            sx={{ mt: 2 }}
          >
            Limpiar filtros
          </Button>
        )}
      </Collapse>
    </Paper>
  );
};

FilterPanel.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'select']).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
      placeholder: PropTypes.string,
    })
  ).isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  collapsible: PropTypes.bool,
  title: PropTypes.string,
};

export default FilterPanel;
